import { useState, useCallback, useEffect } from 'react';
import { uploadImages } from '../utils/flowerApi';
import styles from './ImageUpload.module.css';

interface ImageUploadProps {
  /** 已有植物 ID（编辑已有），传 undefined 则新建 */
  succulentPlantId?: string;
  /** 新建时的植物名称 */
  plantName?: string;
  /** 上传成功回调，参数为植物 ID */
  onSuccess: (succulentPlantId: string) => void;
  /** 关闭弹窗 */
  onClose: () => void;
}

/**
 * 图片上传悬浮面板，支持拖拽和点击选择多文件
 */
export const ImageUpload: React.FC<ImageUploadProps> = ({
  succulentPlantId,
  plantName,
  onSuccess,
  onClose,
}) => {
  const isEditing = !!succulentPlantId;
  const [name, setName] = useState(plantName || '');
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [closing, setClosing] = useState(false);

  // 触发关闭动画
  const handleClose = useCallback(() => {
    setClosing(true);
  }, []);

  // 动画结束后通知父组件
  useEffect(() => {
    if (!closing) return;
    const timer = setTimeout(onClose, 250);
    return () => clearTimeout(timer);
  }, [closing, onClose]);

  const handleFiles = useCallback((newFiles: FileList | null) => {
    if (newFiles) {
      setFiles((prev) => [...prev, ...Array.from(newFiles)]);
    }
  }, []);

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    if (!isEditing && !name.trim()) {
      setError('请输入多肉名称');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const resultId = await uploadImages(files, succulentPlantId, name.trim() || undefined);
      setFiles([]);
      onSuccess(resultId || succulentPlantId || '');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : '上传失败');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={`${styles.overlay} ${closing ? styles.closing : ''}`} onClick={handleClose}>
      <div className={`${styles.modal} ${closing ? styles.closing : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h3>{isEditing ? '上传照片' : '添加多肉'}</h3>
          <button className={styles.closeBtn} onClick={handleClose}>&times;</button>
        </div>

        {/* Plant name input (only for new plants) */}
        {!isEditing && (
          <div className={styles.nameField}>
            <label>多肉名称</label>
            <input
              type="text"
              placeholder="请输入多肉名称"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={uploading}
            />
          </div>
        )}

        {/* Drop zone */}
        <div className={styles.dropZoneSection}>
          <div className={styles.sectionLabel}>多肉图片</div>
          <div
            className={`${styles.dropZone} ${dragOver ? styles.dragOver : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
          >
            <input
              type="file"
              multiple
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/svg+xml"
              onChange={(e) => handleFiles(e.target.files)}
              className={styles.fileInput}
              id="image-upload-input"
            />
            <label htmlFor="image-upload-input">
              <span className={styles.dropIcon}>+</span>
            </label>
          </div>
        </div>

        {/* File list */}
        {files.length > 0 && (
          <div className={styles.fileList}>
            {files.map((file, index) => (
              <div key={index} className={styles.fileItem}>
                <img
                  src={URL.createObjectURL(file)}
                  alt={file.name}
                  className={styles.fileThumb}
                />
                <span className={styles.fileName}>{file.name}</span>
                <button
                  className={styles.removeBtn}
                  onClick={() => removeFile(index)}
                  disabled={uploading}
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
        )}

        {error && <p className={styles.error}>{error}</p>}

        <div className={styles.footer}>
          <button onClick={handleClose} disabled={uploading}>取消</button>
          <button
            className={styles.uploadBtn}
            onClick={handleUpload}
            disabled={files.length === 0 || uploading}
          >
            {uploading ? '上传中...' : '确定'}
          </button>
        </div>
      </div>
    </div>
  );
};
